import React from "react";

/**
 * Lightweight SEO component — sets <title> and meta tags directly on
 * document.head without pulling in react-helmet. Updates on every render
 * and cleans up appended meta tags on unmount.
 */
export default function SEO({
  title,
  description,
  image,
  url,
  type = "website",
  siteName = "Across Indonesia",
}) {
  const fullTitle = title
    ? `${title} · ${siteName}`
    : `${siteName} — Bespoke inbound journeys`;

  React.useEffect(() => {
    if (!document) return;

    const prevTitle = document.title;
    document.title = fullTitle;

    const upsert = (selector, attrs) => {
      let el = document.head.querySelector(selector);
      let created = false;
      if (!el) {
        el = document.createElement("meta");
        created = true;
        Object.entries(attrs).forEach(([k, v]) => {
          if (k !== "content") el.setAttribute(k, v);
        });
        document.head.appendChild(el);
      }
      el.setAttribute("content", attrs.content || "");
      return { el, created };
    };

    const refs = [];

    if (description) {
      refs.push(upsert('meta[name="description"]', {
        name: "description",
        content: description,
      }));
      refs.push(upsert('meta[property="og:description"]', {
        property: "og:description",
        content: description,
      }));
      refs.push(upsert('meta[name="twitter:description"]', {
        name: "twitter:description",
        content: description,
      }));
    }

    refs.push(upsert('meta[property="og:title"]', {
      property: "og:title",
      content: fullTitle,
    }));
    refs.push(upsert('meta[name="twitter:title"]', {
      name: "twitter:title",
      content: fullTitle,
    }));
    refs.push(upsert('meta[property="og:type"]', {
      property: "og:type",
      content: type,
    }));
    refs.push(upsert('meta[property="og:site_name"]', {
      property: "og:site_name",
      content: siteName,
    }));

    if (image) {
      refs.push(upsert('meta[property="og:image"]', {
        property: "og:image",
        content: image,
      }));
      refs.push(upsert('meta[name="twitter:image"]', {
        name: "twitter:image",
        content: image,
      }));
      refs.push(upsert('meta[name="twitter:card"]', {
        name: "twitter:card",
        content: "summary_large_image",
      }));
    }

    if (url) {
      refs.push(upsert('meta[property="og:url"]', {
        property: "og:url",
        content: url,
      }));
    }

    return () => {
      document.title = prevTitle;
      // Only remove the ones we created in this mount — leave persisted
      // tags (like description from index.html) intact.
      refs.forEach(({ el, created }) => {
        if (created && el && el.parentNode) el.parentNode.removeChild(el);
      });
    };
  }, [fullTitle, description, image, url, type, siteName]);

  return null;
}
